import axios from 'axios';
import qs from 'qs';
import { containsAtLeastOne } from 'utils/general';
import { APP } from 'config';
import { onError } from 'utils/query-client';
import { auth } from '../auth/index';

const contexts = ['melp', 'admin', 'public'] as const;
type Context = (typeof contexts)[number];

const versions = ['v1', 'v2', 'v3'] as const;
type Version = (typeof versions)[number];

declare module 'axios' {
  export interface AxiosRequestConfig {
    useAuth?: boolean;
    context?: Context;
    version?: Version;
  }
}

const instance = axios.create({
  baseURL: APP.host.api,
});

export const api = instance.request;

export const fetcher = async <R = any>(url: string) => {
  const res = await api<R>({ url });
  return res.data;
};

instance.interceptors.request.use(
  async ({
    useAuth = true,
    context = 'admin',
    version = 'v1',
    url,
    ...config
  }) => {
    if (useAuth) {
      const authToken = await auth.getToken();
      config.headers.common['Authorization'] = `Bearer ${authToken}`;
    }

    const endpoint =
      containsAtLeastOne(url?.split('/') ?? [], contexts) ||
      containsAtLeastOne(url?.split('/') ?? [], versions)
        ? url
        : `${context}/${version}/${url}`;

    config.paramsSerializer = (params) =>
      qs.stringify(params, {
        arrayFormat: 'brackets',
        encodeValuesOnly: true,
        skipNulls: true,
      });

    return {
      ...config,
      url: endpoint,
    };
  },
);

instance.interceptors.response.use((response) => response, onError);

export const makeRequest = <T = any, D = any>(
  method: 'patch' | 'delete' | 'post' | 'put' | 'get',
  url: string,
  data?: D,
  headers?: {
    'Content-Type'?: string;
    'Access-Control-Allow-Origin'?: string;
  },
  responseType?: any,
) => {
  return instance.request<T>({
    method,
    url,
    data,
    headers,
    responseType,
  });
};
